/**
 * Il Reference Id di un prodotto del catalogo ZDHC: che forma ha, e quando vale.
 *
 * Stava scritto in due posti che non si parlavano — la validazione dell'input
 * manuale in `ProductAssociationSection` e la normalizzazione in `matchStatus`,
 * che il formato non lo guardava affatto. Il formato e' la stessa conoscenza per
 * entrambi, e due copie di una regola divergono appena una viene corretta.
 */

/**
 * La forma che il backend impone (`ZdhcGatewayService::PRODUCT_REFERENCE_PATTERN`).
 *
 * Non e' una regola nostra piu' severa della sua: l'api la applica sia alla
 * lettura del dettaglio (`Zdhc/ProductCatalogueController@show` risponde 422)
 * sia al payload dell'inventario (`Zdhc/InventoryController`). Un id che non la
 * rispetta non e' spendibile da **nessuna** delle due porte, quindi trattarlo
 * come assente non toglie niente a nessuno.
 */
export const REFERENCE_ID_PATTERN = /^20-[A-Z0-9]{8,16}-[A-Z0-9]$/i;

/**
 * Il Reference Id se c'e' qualcosa di scritto, altrimenti `null`. Il formato
 * non lo guarda.
 *
 * Due cose valgono come assenza:
 *
 * 1. un tipo diverso da stringa — il contratto e' `string|null`, e un `{}`
 *    arrivato per sbaglio finirebbe in un URL come `[object Object]`;
 * 2. una stringa di soli spazi, che e' **truthy** e quindi senza normalizzarla
 *    sopprime i fallback e parte verso il catalogo per farsi rifiutare.
 *
 * Un id torna trimmato: gli spazi attorno fanno fallire la lookup, e niente li
 * vieta a monte.
 */
export const presentReferenceId = (value) =>
{
    if(typeof value !== 'string') return null;

    const trimmed = value.trim();

    return trimmed === '' ? null : trimmed;
};

/**
 * Il Reference Id se e' **spendibile**, altrimenti `null`: presente, e con la
 * forma che il Gateway accetta.
 *
 * Distinto da `presentReferenceId` di proposito, e non e' una sfumatura. Chi
 * deve *spendere* l'id — export, schede, payload dell'inventario — con una
 * forma sbagliata non ci fa niente, quindi per lui vale come assente. Chi deve
 * decidere se **rimettere in discussione** lo stato salvato ha bisogno di sapere
 * che qualcosa c'e' scritto: oggi l'api non sa riparare un id malformato
 * (`ZdhcGatewayService::getProduct()` lancia `InvalidArgumentException` prima
 * della richiesta, e `ProductMatchResolver::verifyExistingMatch()` non la
 * cattura), quindi mandarlo a rivalutare produrrebbe un errore a ogni giro
 * invece di una riparazione. Le due funzioni si riuniscono quando l'api sa
 * sciogliere quelle associazioni.
 */
export const usableReferenceId = (value) =>
{
    const present = presentReferenceId(value);

    return present !== null && REFERENCE_ID_PATTERN.test(present) ? present : null;
};
